import React, { useState } from "react";
import axios from "axios";
import { Save, Loader2, Pencil, X } from "lucide-react";
import { API, inputCls, btnPrimary, btnGhost, Field } from "./shared";
import { PANAMA_BANKS } from "../../../data/banks";

const METHOD_OPTIONS = [
  { id: "tarjetas", label: "Tarjetas Visa/Mastercard" },
  { id: "yappy", label: "Yappy" },
  { id: "ach", label: "Transferencias ACH" },
  { id: "suscripciones", label: "Suscripciones" },
];

const toForm = (app) => ({
  business: { name: app.business?.name || "", ruc: app.business?.ruc || "", category: app.business?.category || "", website: app.business?.website || "" },
  representative: {
    full_name: app.representative?.full_name || "", doc_id: app.representative?.doc_id || "", nationality: app.representative?.nationality || "",
    email: app.representative?.email || "", phone: app.representative?.phone || "",
  },
  payment: {
    methods: app.payment?.methods || [], monthly_volume: app.payment?.monthly_volume || "", bank: app.payment?.bank || "",
    account_number: app.payment?.account_number || "", account_type: app.payment?.account_type || "",
  },
});

const Section = ({ title, children }) => (
  <div className="bg-white border border-black/[0.06] rounded-2xl p-4 sm:p-5">
    <p className="text-[11px] font-bold uppercase tracking-wider text-[#0fb013] mb-4">{title}</p>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
  </div>
);

const ClientSummary = ({ app, headers, canManage, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => toForm(app));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (section, key, value) => setForm((f) => ({ ...f, [section]: { ...f[section], [key]: value } }));
  const toggleMethod = (id) =>
    set("payment", "methods", form.payment.methods.includes(id) ? form.payment.methods.filter((m) => m !== id) : [...form.payment.methods, id]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        business: form.business,
        representative: form.representative,
        payment: { ...form.payment, account_type: form.payment.account_type || null },
      };
      const res = await axios.put(`${API}/crm/clients/${app.id}`, payload, { headers });
      onUpdated(res.data);
      setEditing(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "No se pudo guardar. Revisa los campos");
    } finally {
      setSaving(false);
    }
  };

  const ro = !editing;
  const cls = `${inputCls} ${ro ? "bg-[#fafcf9] border-transparent" : ""}`;

  return (
    <div className="space-y-4" data-testid="crm-summary">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[13px] font-semibold text-black/55">Datos del cliente {app.rejection_reason ? <span className="ml-2 text-[#ff603e]">· Motivo de rechazo: {app.rejection_reason}</span> : null}</p>
        {canManage && (editing ? (
          <div className="flex gap-2">
            <button data-testid="crm-cancel-edit" className={btnGhost} onClick={() => { setForm(toForm(app)); setEditing(false); }}><X size={13} /> Cancelar</button>
            <button data-testid="crm-save" className={btnPrimary} onClick={save} disabled={saving}>{saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar</button>
          </div>
        ) : (
          <button data-testid="crm-edit" className={btnGhost} onClick={() => setEditing(true)}><Pencil size={13} /> Editar</button>
        ))}
      </div>
      {error && <p className="text-[12px] font-bold text-[#ff603e] bg-[#ff603e]/10 px-3 py-2 rounded-xl">{error}</p>}

      <Section title="Negocio">
        <Field label="Nombre"><input data-testid="crm-business-name" className={cls} readOnly={ro} value={form.business.name} onChange={(e) => set("business", "name", e.target.value)} /></Field>
        <Field label="RUC"><input className={cls} readOnly={ro} value={form.business.ruc} onChange={(e) => set("business", "ruc", e.target.value)} /></Field>
        <Field label="Tipo de cliente"><input className={cls} readOnly={ro} value={form.business.category} onChange={(e) => set("business", "category", e.target.value)} /></Field>
        <Field label="Sitio web"><input data-testid="crm-business-website" className={cls} readOnly={ro} value={form.business.website} onChange={(e) => set("business", "website", e.target.value)} /></Field>
      </Section>

      <Section title="Representante legal">
        <Field label="Nombre completo"><input className={cls} readOnly={ro} value={form.representative.full_name} onChange={(e) => set("representative", "full_name", e.target.value)} /></Field>
        <Field label="Cédula / Pasaporte"><input className={cls} readOnly={ro} value={form.representative.doc_id} onChange={(e) => set("representative", "doc_id", e.target.value)} /></Field>
        <Field label="Nacionalidad"><input className={cls} readOnly={ro} value={form.representative.nationality} onChange={(e) => set("representative", "nationality", e.target.value)} /></Field>
        <Field label="Correo"><input className={cls} readOnly={ro} value={form.representative.email} onChange={(e) => set("representative", "email", e.target.value)} /></Field>
        <Field label="Teléfono"><input className={cls} readOnly={ro} value={form.representative.phone} onChange={(e) => set("representative", "phone", e.target.value)} /></Field>
      </Section>

      <Section title="Cobros y banco">
        <Field label="Métodos de pago" className="sm:col-span-2 lg:col-span-3">
          <div className="flex flex-wrap gap-2">
            {METHOD_OPTIONS.map((m) => {
              const on = form.payment.methods.includes(m.id);
              return (
                <button key={m.id} type="button" disabled={ro} onClick={() => toggleMethod(m.id)}
                  className={`text-[11px] font-bold px-3 py-1.5 rounded-full border-2 transition-colors ${on ? "bg-[#0fe814]/15 border-[#0fe814] text-[#0fb013]" : "border-black/10 text-black/50"} ${ro ? "cursor-default" : "hover:border-black/40"}`}>
                  {m.label}
                </button>
              );
            })}
          </div>
        </Field>
        <Field label="Volumen mensual"><input className={cls} readOnly={ro} value={form.payment.monthly_volume} onChange={(e) => set("payment", "monthly_volume", e.target.value)} /></Field>
        <Field label="Banco de Banco (CashOut)">
          {ro ? <input className={cls} readOnly value={form.payment.bank} /> : (
            <>
              <input className={cls} list="crm-banks" value={form.payment.bank} onChange={(e) => set("payment", "bank", e.target.value)} placeholder="Selecciona o escribe" />
              <datalist id="crm-banks">{PANAMA_BANKS.map((b) => <option key={b} value={b} />)}</datalist>
            </>
          )}
        </Field>
        <Field label="Número de cuenta"><input className={cls} readOnly={ro} value={form.payment.account_number} onChange={(e) => set("payment", "account_number", e.target.value)} /></Field>
        <Field label="Tipo de cuenta">
          <select className={cls} disabled={ro} value={form.payment.account_type} onChange={(e) => set("payment", "account_type", e.target.value)}>
            <option value="">—</option>
            <option value="ahorro">Cuenta de Ahorro</option>
            <option value="corriente">Cuenta Corriente</option>
          </select>
        </Field>
      </Section>
    </div>
  );
};

export default ClientSummary;
